/*
This file is part of the Notesnook project (https://notesnook.com/)

Copyright (C) 2023 Streetwriters (Private) Limited

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU General Public License for more details.

You should have received a copy of the GNU General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import "./polyfills";
import "./app.css";
import { ThemeDark, ThemeLight, themeToCSS } from "@notesnook/theme";
import Config from "./utils/config";
import { setI18nGlobal, Messages } from "@notesnook/intl";
import { i18n } from "@lingui/core";
import {
  getWindowErrorMessage,
  suppressBenignWindowError
} from "./utils/benign-errors";
import {
  isStaleChunkError,
  persistAppVersionAfterLoad
} from "./utils/stale-chunk";

function recoverOrIgnore(event: ErrorEvent | PromiseRejectionEvent) {
  if (suppressBenignWindowError(event)) return true;
  const message = getWindowErrorMessage(event);
  if (isStaleChunkError(message)) {
    event.preventDefault();
    if ("stopImmediatePropagation" in event) {
      event.stopImmediatePropagation();
    }
    return true;
  }
  return false;
}

// Capture phase so GlobalErrorHandler never turns this into a blank error page.
window.addEventListener(
  "error",
  (event) => {
    recoverOrIgnore(event);
  },
  true
);

window.addEventListener(
  "unhandledrejection",
  (event) => {
    recoverOrIgnore(event);
  },
  true
);

window.addEventListener("error", (event) => {
  recoverOrIgnore(event);
});

const colorScheme = JSON.parse(
  window.localStorage.getItem("colorScheme") || '"light"'
);
const root = document.querySelector("html");
if (root) root.setAttribute("data-theme", colorScheme);

const theme =
  colorScheme === "dark"
    ? Config.get("theme:dark", ThemeDark)
    : Config.get("theme:light", ThemeLight);
const stylesheet = document.getElementById("theme-colors");
if (theme) {
  const css = themeToCSS(theme);
  if (stylesheet) stylesheet.innerHTML = css;
} else stylesheet?.remove();

const locale = import.meta.env.DEV
  ? import("@notesnook/intl/locales/$pseudo-LOCALE.json")
  : import("@notesnook/intl/locales/$en.json");
locale.then(({ default: locale }) => {
  i18n.load({
    en: locale.messages as unknown as Messages
  });
  i18n.activate("en");

  void startApplication();
});
setI18nGlobal(i18n);

function showBootFailure(message: string) {
  const splash = document.getElementById("splash");
  if (!splash) return;
  const label = splash.querySelector("span");
  if (label) {
    label.textContent = message;
  }
}

async function startApplication() {
  try {
    performance.mark("import:root");
    const { startApp } = await import("./root");
    performance.mark("start:app");
    await startApp();
    persistAppVersionAfterLoad();
    try {
      sessionStorage.removeItem("notesnook:chunk-reload");
    } catch {
      /* ignore */
    }
  } catch (e) {
    const message =
      e instanceof Error ? e.message : "Failed to load the app. Please reload.";
    showBootFailure(
      isStaleChunkError(message)
        ? "Update available — please reload the page (Ctrl+Shift+R)."
        : message
    );
    throw e;
  }
}
